import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Droplets, 
  Activity, 
  Filter, 
  FlaskConical, 
  Shield, 
  Sparkles, 
  Leaf, 
  Wind, 
  Tablet, 
  Wifi, 
  CheckCircle2,
  ArrowRight 
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      id: 1,
      icon: Droplets,
      title: "Input & Output TDS Monitoring",
      tagline: "Real-Time Water Quality Tracking",
      description: "Our advanced dual TDS sensors continuously monitor both incoming and purified water quality. The system displays real-time Total Dissolved Solids (TDS) levels, ensuring your input water quality is tracked and the output meets the optimal range of 50-150 ppm for drinking. Get instant alerts if TDS levels deviate from safe parameters, giving you complete visibility into your water purification effectiveness.",
      benefits: [
        "Continuous monitoring of input water quality",
        "Output TDS maintained at optimal 50-150 ppm",
        "Instant alerts for quality deviations",
        "Historical TDS data tracking via app"
      ],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    },
    {
      id: 2,
      icon: Activity,
      title: "Pump & Electronics Health Monitoring",
      tagline: "Predictive Maintenance Intelligence",
      description: "Advanced IoT sensors continuously monitor the health of your RO pump, solenoid valves, and all electronic components. The system tracks operating hours, performance efficiency, voltage fluctuations, and component stress levels. This predictive monitoring identifies potential issues before they cause failures, scheduling maintenance proactively to prevent unexpected breakdowns.",
      benefits: [
        "Real-time pump performance tracking",
        "Electronic component health diagnostics",
        "Predictive failure alerts",
        "Reduced downtime with proactive maintenance"
      ],
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158"
    },
    {
      id: 3,
      icon: Filter,
      title: "Filter Cartridge Consumption Tracking",
      tagline: "Never Miss a Filter Change",
      description: "Smart sensors monitor the actual usage and efficiency of each filtration stage including sediment filters, carbon filters, RO membrane, and post-carbon filters. The system calculates remaining filter life based on water processed, not just time elapsed. You receive timely notifications before filters need replacement, ensuring optimal purification performance and preventing contamination from exhausted filters.",
      benefits: [
        "Individual tracking of all filter stages",
        "Usage-based filter life calculation",
        "Automated replacement reminders",
        "Maintains optimal purification efficiency"
      ],
      image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449"
    },
    {
      id: 4,
      icon: FlaskConical,
      title: "Alkaline pH Balance Maintenance",
      tagline: "Perfect pH for Health",
      description: "Our integrated alkaline mineralizer maintains water pH between 7.5 to 9.5, creating mildly alkaline water that's optimal for human consumption. The system continuously monitors and adjusts pH levels, counteracting the natural acidity of RO water. Alkaline water helps neutralize body acid, improves hydration, supports better digestion, and promotes overall wellness by maintaining your body's ideal pH balance.",
      benefits: [
        "Automated pH maintenance at 7.5-9.5 range",
        "Continuous pH monitoring and adjustment",
        "Enhanced hydration and absorption",
        "Supports digestive health and wellness"
      ],
      image: "https://images.unsplash.com/photo-1532634922-8fe0b757fb13"
    },
    {
      id: 5,
      icon: Shield,
      title: "Enhanced ORP (Antioxidant Property)",
      tagline: "Powerful Antioxidant Water",
      description: "The Oxidation-Reduction Potential (ORP) enhancement system creates water with powerful antioxidant properties. Negative ORP values (-50 to -200 mV) indicate antioxidant-rich water that helps neutralize harmful free radicals in your body. This technology infuses water with extra electrons, creating antioxidant water that supports immune function, reduces oxidative stress, slows aging, and promotes cellular health.",
      benefits: [
        "Negative ORP for antioxidant benefits",
        "Neutralizes free radicals naturally",
        "Supports immune system function",
        "Promotes anti-aging at cellular level"
      ],
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef"
    },
    {
      id: 6,
      icon: Sparkles,
      title: "Essential Mineral Enrichment",
      tagline: "Copper, Calcium & More",
      description: "While RO purification removes impurities, our advanced mineralizer adds back essential minerals that your body needs. The system infuses water with optimal levels of copper (promotes heart health), calcium (strengthens bones), magnesium (supports nerve function), potassium (regulates blood pressure), and trace minerals. This creates mineral-rich alkaline water that's not just pure, but nutritionally enhanced for better health.",
      benefits: [
        "Copper for cardiovascular health",
        "Calcium for bone strength",
        "Magnesium for nerve function",
        "Balanced mineral composition"
      ],
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56"
    },
    {
      id: 7,
      icon: Wind,
      title: "Ozone Treatment for Produce",
      tagline: "Chemical-Free Cleaning",
      description: "Integrated ozone generator produces activated oxygen (O3) for treating fruits and vegetables. Ozone is a powerful natural oxidizer that eliminates pesticides, chemical residues, bacteria, and harmful pathogens from produce surfaces without leaving any chemical traces. Simply place fruits or vegetables in ozone-infused water for 10-15 minutes to achieve up to 99.9% removal of pesticides and microorganisms, making your food truly clean and safe.",
      benefits: [
        "Removes up to 99.9% pesticides from produce",
        "Eliminates bacteria and pathogens naturally",
        "No chemical residue or taste",
        "Extends freshness of fruits & vegetables"
      ],
      image: "https://images.unsplash.com/photo-1610832958506-aa56368176cf"
    },
    {
      id: 8,
      icon: Leaf,
      title: "Hydrogen Water Enrichment",
      tagline: "Next-Gen Hydrogenation",
      description: "Advanced hydrogen infusion technology dissolves molecular hydrogen (H2) into water, creating hydrogen-rich water with exceptional health benefits. Molecular hydrogen is the smallest antioxidant that can penetrate cells and mitochondria. It reduces inflammation, enhances athletic performance, improves energy levels, supports cognitive function, and promotes faster recovery. Experience water that's not just pure, but therapeutically enhanced.",
      benefits: [
        "Enhanced cellular energy production",
        "Reduced inflammation and oxidative stress",
        "Improved athletic performance & recovery",
        "Better cognitive function and focus"
      ],
      image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19"
    },
    {
      id: 9,
      icon: Tablet,
      title: "Interactive Onboard Display",
      tagline: "Know What You're Drinking",
      description: "Large, full-color touchscreen display shows comprehensive water quality information in real-time. See input TDS, output TDS, pH levels, ORP values, filter life status, water consumed today, tank level, and system health—all at a glance. The intuitive interface lets you access detailed reports, adjust settings, and view historical data directly from the unit. Complete transparency about your water quality, displayed beautifully.",
      benefits: [
        "Real-time water quality visualization",
        "Touch-based intuitive interface",
        "Comprehensive system status at a glance",
        "Historical data and trends display"
      ],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    },
    {
      id: 10,
      icon: Wifi,
      title: "Cloud Platform & Mobile App",
      tagline: "Real-Time Telemetry & Control",
      description: "Your RO system connects to Niloman HX Cloud Platform via Wi-Fi/4G, providing comprehensive remote monitoring and control through our mobile app. Access real-time telemetry data from anywhere—check water quality, monitor filter status, view consumption patterns, and receive predictive maintenance alerts. The AI-powered platform analyzes usage patterns to schedule service proactively, order filters automatically, and ensure your system is always operating at peak performance.",
      benefits: [
        "Remote monitoring from anywhere via app",
        "Real-time alerts and notifications",
        "AI-powered predictive maintenance",
        "Automated service scheduling"
      ],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    },
    {
      id: 11,
      icon: CheckCircle2,
      title: "Maximum Uptime & Peace of Mind",
      tagline: "Reliability You Can Trust",
      description: "Our comprehensive approach combining IoT monitoring, predictive maintenance, automated alerts, and proactive service ensures maximum system uptime. The platform predicts issues before they occur, schedules maintenance during convenient times, and dispatches service technicians with the right parts. With 99.9% uptime guarantee, automated filter ordering, and 24/7 system monitoring, you get complete peace of mind knowing your family always has access to pure, safe, healthy water.",
      benefits: [
        "99.9% system uptime guarantee",
        "Proactive issue resolution",
        "Automated parts ordering & delivery",
        "24/7 monitoring and support"
      ],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="bg-white text-cyan-600 mb-6">
              Advanced Features
            </Badge>
            <h1 className="text-5xl font-bold mb-6">
              Revolutionary Water Purification Technology
            </h1>
            <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
              Discover how Niloman HX combines IoT intelligence with advanced purification science to deliver the world's most sophisticated smart water purification system
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isEven = index % 2 === 0;

              return (
                <div 
                  key={feature.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center`}
                >
                  {/* Image */}
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl blur-2xl opacity-20"></div>
                      <img 
                        src={feature.image}
                        alt={feature.title}
                        className="relative rounded-3xl shadow-2xl w-full h-[400px] object-cover border-4 border-white"
                      />
                      {/* Feature Number Badge */}
                      <div className="absolute -top-6 -left-6 w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <span className="text-2xl font-bold text-white">{feature.id}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${!isEven ? 'lg:order-1' : ''} space-y-6`}>
                    <div>
                      <div className="flex items-center gap-4 mb-3">
                        <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
                          <Icon className="h-7 w-7 text-white" />
                        </div>
                        <Badge className="bg-cyan-100 text-cyan-700 border-0">
                          {feature.tagline}
                        </Badge>
                      </div>
                      
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {feature.title}
                      </h2>
                      
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>

                    {/* Benefits Card */}
                    <Card className="border-2 border-cyan-200 bg-gradient-to-br from-white to-cyan-50">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-teal-600" />
                          Key Benefits
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {feature.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="w-6 h-6 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <div className="w-2 h-2 bg-cyan-600 rounded-full"></div>
                              </div>
                              <span className="text-gray-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Complete Technology Integration
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All features work seamlessly together to provide unparalleled water purification
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Droplets, title: "Advanced Purification", desc: "Multi-stage RO + UV + UF" },
              { icon: Sparkles, title: "Mineralization", desc: "Essential minerals added back" },
              { icon: FlaskConical, title: "pH Balance", desc: "Alkaline water 7.5-9.5" },
              { icon: Shield, title: "Antioxidant Water", desc: "Negative ORP benefits" },
              { icon: Leaf, title: "Hydrogen Infusion", desc: "Therapeutic enhancement" },
              { icon: Wifi, title: "IoT Connected", desc: "Cloud & app integration" }
            ].map((tech, idx) => {
              const Icon = tech.icon;
              return (
                <Card key={idx} className="text-center border-2 hover:border-cyan-400 transition-all duration-300 hover:shadow-lg">
                  <CardHeader>
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{tech.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{tech.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Experience the Future of Water Purification
          </h2>
          <p className="text-xl mb-8 text-cyan-50">
            See these advanced features in action. Book a free demo and discover the Niloman HX difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = '/contact'}
              size="lg" 
              className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8"
            >
              Book Free Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => window.location.href = '/products'}
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/10 text-lg px-8"
            >
              View Products
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
